import { Alert, Box, Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useCallback } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton.tsx";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import biApi from "../../../../../../../../../api/biApi.ts";
import UsedByReports from "./UsedByReports.tsx";
import type { MeasureDescriptor } from "../../../../../../../../../api/types/svTypes.ts";

interface Props {
  measure: MeasureDescriptor;
  onClose: () => void;
}

const UsedByReportsDialog = ({ measure, onClose }: Props) => {
  const getCustomMeasureReportsUsage = useCallback(
    () => biApi.getUsedByReports(measure.measureId ?? measure.name),
    [measure.measureId, measure.name]
  );
  const [reportsUsageResponse, fetchError, { isFetching }] = useFetch(getCustomMeasureReportsUsage);

  return (
    <Dialog open fullWidth slotProps={{ paper: { sx: { maxWidth: 514, minHeight: 205, height: "auto" } } }}>
      <DialogTitle>
        Custom Measure Where-Used List
        <TypographyTooltipEllipsis text={measure.caption} typographyProps={{ color: "secondary" }} />
      </DialogTitle>
      <DialogCloseButton onClick={onClose} />
      {isFetching && !fetchError && (
        <Box sx={{ px: 3, py: 2.5 }}>
          <InlineLoader />
        </Box>
      )}
      {fetchError && !isFetching && (
        <Box sx={{ px: 3, py: 2.5 }}>
          <Alert severity="error" sx={{ width: "100%" }}>
            Couldn’t get the reports usage information
          </Alert>
        </Box>
      )}
      {!fetchError && !isFetching && reportsUsageResponse && (
        <DialogContent>
          <UsedByReports
            reports={reportsUsageResponse.reports}
            hiddenReportsCount={reportsUsageResponse.hiddenReportsCount}
            minRows={3}
            maxRows={10}
          />
        </DialogContent>
      )}
      <DialogButtons>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default UsedByReportsDialog;
